/**
 * MultiAudioFileList — shared multi-file audio upload UI for the creator
 * wizard. Handles drag-and-drop / browse, ordered file list with up/down
 * reorder + remove, and client-side validation (count, MIME consistency,
 * per-file size, total size). The actual concat is server-side.
 */

import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MultiAudioFileList.css';

const ACCEPTED_TYPES = [
  'audio/mp3',
  'audio/wav',
  'audio/m4a',
  'audio/mpeg',
  'audio/x-m4a',
];
const ACCEPTED_EXTENSIONS = '.mp3,.wav,.m4a';
const DEFAULT_MAX_FILES = 10;
const DEFAULT_MAX_FILE_SIZE = 50 * 1024 * 1024;       // 50MB
const DEFAULT_MAX_TOTAL_SIZE = 200 * 1024 * 1024;     // 200MB

interface MultiAudioFileListProps {
  files: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
  maxFileSize?: number;
  maxTotalSize?: number;
  disabled?: boolean;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
};

const MultiAudioFileList: React.FC<MultiAudioFileListProps> = ({
  files,
  onChange,
  maxFiles = DEFAULT_MAX_FILES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxTotalSize = DEFAULT_MAX_TOTAL_SIZE,
  disabled = false,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateAndMerge = useCallback(
    (incoming: File[]): { ok: boolean; merged?: File[]; error?: string } => {
      if (incoming.length === 0) return { ok: true, merged: files };

      // Combined list (existing + incoming)
      const combined = [...files, ...incoming];

      if (combined.length > maxFiles) {
        return {
          ok: false,
          error: t('creator:workflow.audioUpload.tooManyFiles', { max: maxFiles }),
        };
      }

      // Per-file size + format check
      for (const f of incoming) {
        if (f.size > maxFileSize) {
          return {
            ok: false,
            error: t('creator:workflow.audioUpload.fileTooLarge', {
              name: f.name,
              max: formatBytes(maxFileSize),
            }),
          };
        }
        if (!ACCEPTED_TYPES.includes(f.type)) {
          return {
            ok: false,
            error: t('creator:workflow.audioUpload.invalidAudioFormat', {
              name: f.name,
              type: f.type || 'unknown',
            }),
          };
        }
      }

      // MIME consistency check (server requires same format)
      const baseMime = combined[0].type;
      for (const f of combined) {
        if (f.type !== baseMime) {
          return {
            ok: false,
            error: t('creator:workflow.audioUpload.fileFormatMismatch'),
          };
        }
      }

      // Total size cap
      const totalSize = combined.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > maxTotalSize) {
        return {
          ok: false,
          error: t('creator:workflow.audioUpload.totalSizeTooLarge', {
            max: formatBytes(maxTotalSize),
          }),
        };
      }

      return { ok: true, merged: combined };
    },
    [files, maxFiles, maxFileSize, maxTotalSize, t],
  );

  const addFiles = useCallback(
    (incoming: File[]) => {
      setError(null);
      const result = validateAndMerge(incoming);
      if (!result.ok) {
        setError(result.error || 'Invalid file selection');
        return;
      }
      if (result.merged) onChange(result.merged);
    },
    [validateAndMerge, onChange],
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length > 0) addFiles(selected);
    // Reset input so selecting the same file again triggers onChange
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) addFiles(dropped);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const removeFile = (idx: number) => {
    setError(null);
    onChange(files.filter((_, i) => i !== idx));
  };

  const moveFile = (idx: number, direction: -1 | 1) => {
    const target = idx + direction;
    if (target < 0 || target >= files.length) return;
    const next = [...files];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  const canAddMore = files.length < maxFiles && !disabled;

  return (
    <div className="multi-audio-file-list">
      {/* Dropzone */}
      <div
        className={`multi-audio-dropzone ${isDragging ? 'is-dragging' : ''} ${disabled ? 'is-disabled' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => canAddMore && inputRef.current?.click()}
        role="button"
        tabIndex={canAddMore ? 0 : -1}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          multiple
          onChange={handleFileInput}
          disabled={!canAddMore}
          style={{ display: 'none' }}
        />
        <div className="multi-audio-dropzone__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>
        <div className="multi-audio-dropzone__primary">
          {files.length === 0
            ? t('creator:workflow.audioUpload.dropAudioHere')
            : t('creator:workflow.audioUpload.addMoreFiles')}
        </div>
        <div className="multi-audio-dropzone__hint">
          {t('creator:workflow.audioUpload.audioFormatsHint')}
          {' • '}
          {t('creator:workflow.audioUpload.multiAudioHint', { max: maxFiles })}
        </div>
      </div>

      {error && (
        <div className="multi-audio-error" role="alert">{error}</div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <ul className="multi-audio-list">
          {files.map((f, idx) => (
            <li key={`${f.name}-${idx}`} className="multi-audio-list__item">
              <span className="multi-audio-list__index">{idx + 1}</span>
              <span className="multi-audio-list__name" title={f.name}>{f.name}</span>
              <span className="multi-audio-list__size">{formatBytes(f.size)}</span>
              <div className="multi-audio-list__actions">
                <button
                  type="button"
                  className="multi-audio-list__btn"
                  onClick={() => moveFile(idx, -1)}
                  disabled={idx === 0 || disabled}
                  aria-label={t('creator:workflow.audioUpload.moveUp')}
                  title={t('creator:workflow.audioUpload.moveUp')}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                    <path d="M7 14l5-5 5 5z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="multi-audio-list__btn"
                  onClick={() => moveFile(idx, 1)}
                  disabled={idx === files.length - 1 || disabled}
                  aria-label={t('creator:workflow.audioUpload.moveDown')}
                  title={t('creator:workflow.audioUpload.moveDown')}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="multi-audio-list__btn multi-audio-list__btn--remove"
                  onClick={() => removeFile(idx)}
                  disabled={disabled}
                  aria-label={t('creator:workflow.audioUpload.removeFile')}
                  title={t('creator:workflow.audioUpload.removeFile')}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {files.length > 0 && (
        <div className="multi-audio-summary">
          {t('creator:workflow.audioUpload.summary', {
            count: files.length,
            size: formatBytes(totalSize),
          })}
        </div>
      )}
    </div>
  );
};

export default MultiAudioFileList;
